import React, { useCallback, useState, useRef, createElement, Component } from 'react';
import { X, Upload, Wand2, AlertCircle, Sparkles, Check, Info, Clock, User, Image as ImageIcon, Zap, Download, Share2, Coins, Heart, Gift, Settings, Sliders, Shuffle } from 'lucide-react';
// Types
interface FaceShiftModalProps {
  onClose: () => void;
  onComplete?: (result: RenderResult) => void;
  ritualId?: string | null;
}
interface RenderResult {
  id: string;
  url: string;
  type: 'image' | 'video' | 'gif';
  prompt: string;
  model: string;
  createdAt: Date;
  hdEnabled: boolean;
  faceOnly: boolean;
  enhanceDetails: boolean;
  creditCost: number;
}
interface ModelOption {
  id: string;
  name: string;
  label: string;
  isHot?: boolean;
  isBeta?: boolean;
  isPro?: boolean;
  description: string;
}
// Component
export const FaceShiftModal: React.FC<FaceShiftModalProps> = ({
  onClose,
  onComplete,
  ritualId
}) => {
  // Refs
  const sourceFileInputRef = useRef<HTMLInputElement>(null);
  const targetFileInputRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // State for tabs
  const [activeTab, setActiveTab] = useState<'face-swap' | 'face-enhance' | 'face-animate'>('face-swap');
  // State for file upload
  const [uploadedSourceFile, setUploadedSourceFile] = useState<File | null>(null);
  const [sourceFilePreview, setSourceFilePreview] = useState<string | null>(null);
  const [uploadedTargetFile, setUploadedTargetFile] = useState<File | null>(null);
  const [targetFilePreview, setTargetFilePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // State for options
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState<string>('faceshift-v2');
  const [hdEnabled, setHdEnabled] = useState(false);
  const [faceOnly, setFaceOnly] = useState(true);
  const [enhanceDetails, setEnhanceDetails] = useState(true);
  // State for rendering
  const [isRendering, setIsRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderResult, setRenderResult] = useState<RenderResult | null>(null);
  const [renderEta, setRenderEta] = useState<string | null>(null);
  // State for credits
  const [userCredits, setUserCredits] = useState({
    desireGems: 120,
    loveTokens: 240
  });
  const [creditCost, setCreditCost] = useState(1);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  // Model options
  const modelOptions: ModelOption[] = [{
    id: 'faceshift-v1',
    name: 'FaceShift v1.0',
    label: 'FaceShift v1.0',
    description: 'Basic face transformation with good quality results.'
  }, {
    id: 'faceshift-v2',
    name: 'FaceShift v2.0',
    label: 'FaceShift v2.0',
    isHot: true,
    description: 'Improved face processing with better detail preservation.'
  }, {
    id: 'faceshift-v3.1',
    name: 'FaceShift v3.1',
    label: 'FaceShift v3.1',
    isBeta: true,
    description: 'Latest model with advanced facial feature understanding.'
  }, {
    id: 'hd-faceshift',
    name: 'HD FaceShift',
    label: 'HD FaceShift',
    isPro: true,
    description: 'Professional-grade face transformations with maximum quality.'
  }];
  // Tab configuration
  const tabs = [{
    id: 'face-swap',
    label: 'Face Swap',
    icon: <Shuffle size={16} data-id="element-1071" />
  }, {
    id: 'face-enhance',
    label: 'Face Enhance',
    icon: <Sparkles size={16} data-id="element-1072" />
  }, {
    id: 'face-animate',
    label: 'Face Animate',
    icon: <Zap size={16} data-id="element-1073" />
  }];
  // Handle file upload
  const handleSourceFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload an image.');
      return;
    }
    if (file.size > maxSize) {
      setUploadError('File too large. Maximum size is 50MB.');
      return;
    }
    setUploadedSourceFile(file);
    setUploadError(null);
    // Create preview
    const reader = new FileReader();
    reader.onload = e => {
      setSourceFilePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  const handleTargetFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload an image.');
      return;
    }
    if (file.size > maxSize) {
      setUploadError('File too large. Maximum size is 50MB.');
      return;
    }
    setUploadedTargetFile(file);
    setUploadError(null);
    // Create preview
    const reader = new FileReader();
    reader.onload = e => {
      setTargetFilePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  // Handle drag and drop
  const handleSourceDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleSourceDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Create a synthetic event to reuse validation logic
      const syntheticEvent = {
        target: {
          files: [file]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleSourceFileUpload(syntheticEvent);
    }
  }, []);
  const handleTargetDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleTargetDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Create a synthetic event to reuse validation logic
      const syntheticEvent = {
        target: {
          files: [file]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleTargetFileUpload(syntheticEvent);
    }
  }, []);
  // Handle tab change
  const handleTabChange = (tabId: 'face-swap' | 'face-enhance' | 'face-animate') => {
    setActiveTab(tabId);
    setUploadedSourceFile(null);
    setSourceFilePreview(null);
    setUploadedTargetFile(null);
    setTargetFilePreview(null);
    setUploadError(null);
    setRenderResult(null);
  };
  // Handle model change
  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const model = e.target.value;
    setSelectedModel(model);
    // Adjust credit cost based on model
    if (model === 'hd-faceshift') {
      setCreditCost(3);
    } else if (model === 'faceshift-v3.1') {
      setCreditCost(2);
    } else {
      setCreditCost(1);
    }
  };
  // Handle HD toggle
  const handleHdToggle = () => {
    const newHdEnabled = !hdEnabled;
    setHdEnabled(newHdEnabled);
    // Adjust credit cost
    if (newHdEnabled) {
      setCreditCost(prev => prev + 1);
    } else {
      setCreditCost(prev => Math.max(1, prev - 1));
    }
  };
  // Handle render
  const handleRender = () => {
    // Check if user has enough credits
    const hasEnoughCredits = userCredits.desireGems >= creditCost || userCredits.loveTokens >= creditCost * 10;
    if (!hasEnoughCredits) {
      setInsufficientCredits(true);
      return;
    }
    // Check if we have required inputs
    if (!uploadedSourceFile) {
      setUploadError('Please upload a source image to continue.');
      return;
    }
    if (activeTab === 'face-swap' && !uploadedTargetFile) {
      setUploadError('Please upload a target image for face swap.');
      return;
    }
    // Start rendering
    setIsRendering(true);
    setRenderProgress(0);
    setRenderEta('Estimating...');
    // Mock rendering progress
    const interval = setInterval(() => {
      setRenderProgress(prev => {
        const newProgress = prev + Math.random() * 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          // Simulate render completion
          setTimeout(() => {
            const result: RenderResult = {
              id: `render-${Date.now()}`,
              url: 'https://placehold.co/600x400/black/white?text=AI+Generated+Face',
              type: 'image',
              prompt,
              model: selectedModel,
              createdAt: new Date(),
              hdEnabled,
              faceOnly,
              enhanceDetails,
              creditCost
            };
            setRenderResult(result);
            setIsRendering(false);
            // Deduct credits
            setUserCredits(prev => ({
              ...prev,
              desireGems: prev.desireGems - creditCost
            }));
            // Call completion callback
            if (onComplete) {
              onComplete(result);
            }
          }, 500);
          return 100;
        }
        // Update ETA
        const remainingPercent = 100 - newProgress;
        const secondsRemaining = Math.round(remainingPercent / 10) * 30;
        setRenderEta(secondsRemaining <= 60 ? `${secondsRemaining} seconds remaining` : `${Math.round(secondsRemaining / 60)} minutes remaining`);
        return newProgress;
      });
    }, 1000);
    // Cleanup on unmount
    return () => clearInterval(interval);
  };
  // Open token store
  const handleOpenTokenStore = () => {
    // Dispatch event to open token store
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'token_wallet'
      }
    });
    document.dispatchEvent(event);
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg animate-in fade-in duration-300" data-id="element-1074">
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden animate-in zoom-in-90 duration-300 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800" data-id="element-1075">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between" data-id="element-1076">
          <div className="flex items-center gap-2" data-id="element-1077">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-500/20 text-blue-500" data-id="element-1078">
              <User size={18} data-id="element-1079" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1080">
              Face Shift
            </h2>
            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded-full" data-id="element-1081">
              AI Powered
            </span>
          </div>
          <div className="flex items-center" data-id="element-1082">
            {activeTab === 'face-swap' && <div className="hidden md:block mr-4 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1083">
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1084">
                  <span className="text-blue-500 dark:text-blue-400 font-medium" data-id="element-1085">
                    Face Swap:
                  </span>{' '}
                  Ethically transfer facial features between images with
                  realistic results
                </p>
              </div>}
            {activeTab === 'face-enhance' && <div className="hidden md:block mr-4 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1086">
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1087">
                  <span className="text-blue-500 dark:text-blue-400 font-medium" data-id="element-1088">
                    Face Enhance:
                  </span>{' '}
                  Improve image quality, fix lighting, and enhance facial
                  details
                </p>
              </div>}
            {activeTab === 'face-animate' && <div className="hidden md:block mr-4 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-1089">
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1090">
                  <span className="text-blue-500 dark:text-blue-400 font-medium" data-id="element-1091">
                    Face Animate:
                  </span>{' '}
                  Bring still photos to life with subtle, realistic expressions
                  and movements
                </p>
              </div>}
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors" onClick={onClose} data-id="element-1092">
              <X size={18} className="text-gray-500 dark:text-gray-400" data-id="element-1093" />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="px-6 pt-4 pb-2 border-b border-gray-200 dark:border-gray-800" data-id="element-1094">
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide" data-id="element-1095">
            {tabs.map(tab => <button key={tab.id} className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50'}`} onClick={() => handleTabChange(tab.id as any)} data-id="element-1096">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
        </div>
        <div className="flex flex-col md:flex-row h-[calc(90vh-8rem)] overflow-hidden" data-id="element-1097">
          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-gray-100 dark:scrollbar-track-gray-900" data-id="element-1098">
            {/* Upload section - Source */}
            <div className="mb-6" data-id="element-1099">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1100">
                {activeTab === 'face-swap' ? 'Source Image (Face to use)' : 'Upload Image'}
              </h3>
              <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center transition-colors ${uploadError ? 'border-red-500/50 bg-red-500/5' : sourceFilePreview ? 'border-green-500/50 bg-green-500/5' : 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600'}`} onDragOver={handleSourceDragOver} onDrop={handleSourceDrop} onClick={() => sourceFileInputRef.current?.click()} data-id="element-1101">
                <input type="file" ref={sourceFileInputRef} className="hidden" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleSourceFileUpload} data-id="element-1102" />
                {sourceFilePreview ? <div className="relative w-full max-w-md" data-id="element-1103">
                    <img src={sourceFilePreview} alt="Source Preview" className="w-full h-auto max-h-64 rounded-lg object-contain" data-id="element-1104" />
                    <button className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white" onClick={e => {
                  e.stopPropagation();
                  setUploadedSourceFile(null);
                  setSourceFilePreview(null);
                }} data-id="element-1105">
                      <X size={16} data-id="element-1106" />
                    </button>
                  </div> : <>
                    <Upload size={36} className="text-gray-400 dark:text-gray-500 mb-2" data-id="element-1107" />
                    <p className="text-gray-600 dark:text-gray-400 text-center mb-1" data-id="element-1108">
                      {activeTab === 'face-swap' ? 'Upload the image with the face you want to use' : 'Drag & drop an image, or click to browse'}
                    </p>
                    <p className="text-gray-500 text-xs text-center" data-id="element-1109">
                      Supports JPG, PNG, GIF, WebP up to 50MB
                    </p>
                  </>}
              </div>
            </div>
            {/* Upload section - Target (only for face-swap) */}
            {activeTab === 'face-swap' && <div className="mb-6" data-id="element-1110">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1111">
                  Target Image (Where to place the face)
                </h3>
                <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center transition-colors ${uploadError && !uploadedSourceFile ? 'border-red-500/50 bg-red-500/5' : targetFilePreview ? 'border-green-500/50 bg-green-500/5' : 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600'}`} onDragOver={handleTargetDragOver} onDrop={handleTargetDrop} onClick={() => targetFileInputRef.current?.click()} data-id="element-1112">
                  <input type="file" ref={targetFileInputRef} className="hidden" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleTargetFileUpload} data-id="element-1113" />
                  {targetFilePreview ? <div className="relative w-full max-w-md" data-id="element-1114">
                      <img src={targetFilePreview} alt="Target Preview" className="w-full h-auto max-h-64 rounded-lg object-contain" data-id="element-1115" />
                      <button className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white" onClick={e => {
                  e.stopPropagation();
                  setUploadedTargetFile(null);
                  setTargetFilePreview(null);
                }} data-id="element-1116">
                        <X size={16} data-id="element-1117" />
                      </button>
                    </div> : <>
                      <Upload size={36} className="text-gray-400 dark:text-gray-500 mb-2" data-id="element-1118" />
                      <p className="text-gray-600 dark:text-gray-400 text-center mb-1" data-id="element-1119">
                        Upload the image where you want to place the face
                      </p>
                      <p className="text-gray-500 text-xs text-center" data-id="element-1120">
                        Supports JPG, PNG, GIF, WebP up to 50MB
                      </p>
                    </>}
                </div>
              </div>}
            {uploadError && <div className="mt-2 mb-6 px-3 py-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-600 dark:text-red-400" data-id="element-1121">
                <AlertCircle size={16} data-id="element-1122" />
                <span data-id="element-1123">{uploadError}</span>
              </div>}
            {/* Prompt - only for face-animate */}
            {activeTab === 'face-animate' && <div className="mb-6" data-id="element-1124">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1125">
                  Animation Instructions
                </h3>
                <div className="relative" data-id="element-1126">
                  <textarea ref={promptRef} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe how you want the face to animate (e.g., 'Smile slightly, then look to the right')" className="w-full h-32 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" maxLength={800} data-id="element-1127" />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-500" data-id="element-1128">
                    {prompt.length}/800
                  </div>
                </div>
                <div className="mt-2 px-3 py-2 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400" data-id="element-1129">
                  <Info size={16} data-id="element-1130" />
                  <span data-id="element-1131">
                    Be specific about expressions, head movements, and timing
                    for best results.
                  </span>
                </div>
              </div>}
            {/* Model selector */}
            <div className="mb-6" data-id="element-1132">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1133">
                Model
              </h3>
              <select value={selectedModel} onChange={handleModelChange} className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }} data-id="element-1134">
                {modelOptions.map(model => <option key={model.id} value={model.id} disabled={model.isPro} data-id="element-1135">
                    {model.label}
                    {model.isHot ? ' (HOT)' : ''}
                    {model.isBeta ? ' (BETA)' : ''}
                    {model.isPro ? ' (PRO)' : ''}
                  </option>)}
              </select>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1136">
                {modelOptions.map(model => model.id === selectedModel && <div key={`info-${model.id}`} className="col-span-full px-3 py-2 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-2 text-sm" data-id="element-1137">
                        {model.isHot ? <Sparkles size={16} className="text-amber-500 dark:text-amber-400" data-id="element-1138" /> : model.isBeta ? <Sparkles size={16} className="text-blue-500 dark:text-blue-400" data-id="element-1139" /> : model.isPro ? <Zap size={16} className="text-purple-500 dark:text-purple-400" data-id="element-1140" /> : <Check size={16} className="text-green-500 dark:text-green-400" data-id="element-1141" />}
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-1142">
                          {model.description}
                        </span>
                      </div>)}
              </div>
            </div>
            {/* Toggles */}
            <div className="mb-6" data-id="element-1143">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1144">
                Options
              </h3>
              <div className="space-y-3" data-id="element-1145">
                <div className="flex items-center justify-between" data-id="element-1146">
                  <div className="flex items-center gap-2" data-id="element-1147">
                    <Zap size={18} className="text-purple-500 dark:text-purple-400" data-id="element-1148" />
                    <span className="text-gray-900 dark:text-white" data-id="element-1149">
                      HD Processing
                    </span>
                    {selectedModel === 'hd-faceshift' && <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 text-xs rounded-full" data-id="element-1150">
                        PRO
                      </span>}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-1151">
                    <input type="checkbox" checked={hdEnabled} onChange={handleHdToggle} className="sr-only peer" disabled={selectedModel === 'hd-faceshift'} data-id="element-1152" />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500" data-id="element-1153"></div>
                  </label>
                </div>
                {activeTab === 'face-swap' && <div className="flex items-center justify-between" data-id="element-1154">
                    <div className="flex items-center gap-2" data-id="element-1155">
                      <User size={18} className="text-blue-500 dark:text-blue-400" data-id="element-1156" />
                      <span className="text-gray-900 dark:text-white" data-id="element-1157">
                        Face only (preserve hair/background)
                      </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-1158">
                      <input type="checkbox" checked={faceOnly} onChange={() => setFaceOnly(!faceOnly)} className="sr-only peer" data-id="element-1159" />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500" data-id="element-1160"></div>
                    </label>
                  </div>}
                {(activeTab === 'face-enhance' || activeTab === 'face-animate') && <div className="flex items-center justify-between" data-id="element-1161">
                    <div className="flex items-center gap-2" data-id="element-1162">
                      <Sparkles size={18} className="text-green-500 dark:text-green-400" data-id="element-1163" />
                      <span className="text-gray-900 dark:text-white" data-id="element-1164">
                        Enhance details
                      </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-1165">
                      <input type="checkbox" checked={enhanceDetails} onChange={() => setEnhanceDetails(!enhanceDetails)} className="sr-only peer" data-id="element-1166" />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500" data-id="element-1167"></div>
                    </label>
                  </div>}
                {activeTab === 'face-enhance' && <div className="mt-4" data-id="element-1168">
                    <div className="flex items-center justify-between mb-2" data-id="element-1169">
                      <div className="flex items-center gap-2" data-id="element-1170">
                        <Sliders size={18} className="text-blue-500 dark:text-blue-400" data-id="element-1171" />
                        <span className="text-gray-900 dark:text-white" data-id="element-1172">
                          Enhancement intensity
                        </span>
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1173">
                        70%
                      </span>
                    </div>
                    <input type="range" min="0" max="100" defaultValue="70" className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-1174" />
                  </div>}
              </div>
            </div>
          </div>
          {/* Right sidebar - Credits and rendering */}
          <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-800 flex flex-col" data-id="element-1175">
            {/* Credit panel */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1176">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1177">
                Credits
              </h3>
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-1178">
                <div className="flex items-center justify-between mb-2" data-id="element-1179">
                  <span className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1180">
                    Cost per process:
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium" data-id="element-1181">
                    {creditCost} credit{creditCost > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-4" data-id="element-1182">
                  <span className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1183">
                    Your balance:
                  </span>
                  <div className="flex items-center gap-3" data-id="element-1184">
                    <div className="flex items-center gap-1" data-id="element-1185">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" data-id="element-1186"></div>
                      <span className="text-gray-900 dark:text-white" data-id="element-1187">
                        {userCredits.desireGems}
                      </span>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-1188">
                      <div className="w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center" data-id="element-1189">
                        <Heart size={10} className="text-white" data-id="element-1190" />
                      </div>
                      <span className="text-gray-900 dark:text-white" data-id="element-1191">
                        {userCredits.loveTokens}
                      </span>
                    </div>
                  </div>
                </div>
                <button className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-2" onClick={handleOpenTokenStore} data-id="element-1192">
                  <Coins size={16} data-id="element-1193" />
                  <span data-id="element-1194">Get more credits</span>
                </button>
              </div>
              {insufficientCredits && <div className="mb-4 px-3 py-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-600 dark:text-red-400" data-id="element-1195">
                  <AlertCircle size={16} data-id="element-1196" />
                  <span data-id="element-1197">Not enough credits. Please top up.</span>
                </div>}
              <button className={`w-full py-3 px-4 rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${isRendering ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'}`} onClick={handleRender} disabled={isRendering} data-id="element-1198">
                {isRendering ? <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" data-id="element-1199"></div>
                    <span data-id="element-1200">Processing...</span>
                  </> : <>
                    <Wand2 size={16} data-id="element-1201" />
                    <span data-id="element-1202">
                      Process • {creditCost} credit{creditCost > 1 ? 's' : ''}
                    </span>
                  </>}
              </button>
            </div>
            {/* Render progress */}
            {isRendering && <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1203">
                <div className="flex items-center justify-between mb-2" data-id="element-1204">
                  <h4 className="text-gray-900 dark:text-white font-medium" data-id="element-1205">
                    Processing
                  </h4>
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-sm" data-id="element-1206">
                    <Clock size={14} data-id="element-1207" />
                    <span data-id="element-1208">{renderEta}</span>
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden mb-2" data-id="element-1209">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{
                width: `${renderProgress}%`
              }} data-id="element-1210"></div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1211">
                  Creating your{' '}
                  {activeTab === 'face-swap' ? 'face swap' : activeTab === 'face-enhance' ? 'enhanced image' : 'animated face'}
                  ...
                </p>
              </div>}
            {/* Result preview */}
            {renderResult && <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1212">
                <h4 className="text-gray-900 dark:text-white font-medium mb-3" data-id="element-1213">
                  Result
                </h4>
                <div className="rounded-lg overflow-hidden mb-3" data-id="element-1214">
                  {renderResult.type === 'video' ? <video src={renderResult.url} className="w-full h-auto" controls autoPlay loop data-id="element-1215" /> : <img src={renderResult.url} alt="Result" className="w-full h-auto" data-id="element-1216" />}
                </div>
                <div className="flex gap-2" data-id="element-1217">
                  <button className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5" onClick={() => {
                // Download logic
                const a = document.createElement('a');
                a.href = renderResult.url;
                a.download = `faceshift-${Date.now()}.${renderResult.type === 'video' ? 'mp4' : 'jpg'}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }} data-id="element-1218">
                    <Download size={14} data-id="element-1219" />
                    <span data-id="element-1220">Download</span>
                  </button>
                  <button className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5" onClick={() => {
                // Share logic
                alert('Share functionality would open here');
              }} data-id="element-1221">
                    <Share2 size={14} data-id="element-1222" />
                    <span data-id="element-1223">Share</span>
                  </button>
                </div>
              </div>}
            {/* Smart nudges */}
            <div className="p-6 flex-1" data-id="element-1224">
              <h4 className="text-gray-900 dark:text-white font-medium mb-3" data-id="element-1225">
                Tips
              </h4>
              <div className="space-y-3" data-id="element-1226">
                <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg flex items-start gap-2 text-sm" data-id="element-1227">
                  <Info size={16} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1228" />
                  <div data-id="element-1229">
                    <p className="text-blue-700 dark:text-blue-300 font-medium mb-1" data-id="element-1230">
                      Better results
                    </p>
                    <p className="text-gray-600 dark:text-gray-400" data-id="element-1231">
                      {activeTab === 'face-swap' ? 'Use high-quality images with clear, forward-facing faces for best results.' : activeTab === 'face-enhance' ? 'Images with good lighting work best. The AI can enhance details but not recover completely missing information.' : 'Be specific about facial expressions in your prompt for more natural animations.'}
                    </p>
                  </div>
                </div>
                {userCredits.desireGems < 5 && <div className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg flex items-start gap-2 text-sm" data-id="element-1232">
                    <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" data-id="element-1233" />
                    <div data-id="element-1234">
                      <p className="text-amber-700 dark:text-amber-300 font-medium mb-1" data-id="element-1235">
                        Top up to continue
                      </p>
                      <p className="text-gray-600 dark:text-gray-400" data-id="element-1236">
                        Your credits are running low. Get more to continue
                        creating.
                      </p>
                    </div>
                  </div>}
                {activeTab === 'face-swap' && <div className="p-3 bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 rounded-lg flex items-start gap-2 text-sm" data-id="element-1237">
                    <Settings size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" data-id="element-1238" />
                    <div data-id="element-1239">
                      <p className="text-purple-700 dark:text-purple-300 font-medium mb-1" data-id="element-1240">
                        Ethical usage
                      </p>
                      <p className="text-gray-600 dark:text-gray-400" data-id="element-1241">
                        Remember to use face swap ethically and with consent.
                        All images are watermarked for transparency.
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};